/* global graphql */

import React, { Fragment, useContext } from "react";
import * as R from "ramda";
import qs from "qs";

import { injectGlobal } from "emotion";

import styled from "react-emotion";
import { translate } from "react-i18next";

import Modal from "react-modal";
import { withRouter } from "react-router";
import "react-dates/initialize";

import { colors } from "src/styles";
import { shortenLocaleName } from "src/utils";

import background from "./background.svg";

import { AuthenticationContext } from "src/banking/AuthenticationContext";
import { AuthenticationError } from "src/graphql";

import {
  Alerts,
  Box,
  Breadcrumb,
  Divider,
  Flex,
  HeaderSlider,
  HelmetBlock,
  PageContainer,
  PixelTracker,
} from "src/components";

import { isActiveArticle } from "src/components/Embed";

import { compose, renderComponent, withProps } from "recompose";

import Footer from "./Footer";
import Header from "./Header";

Modal.setAppElement("#___gatsby");

injectGlobal`
  * { 
    box-sizing: border-box; 
  }
  body {
    padding: 0px;
    margin: 0px;
    background-color: ${colors.background};
    @media print {
      background-color: white;
    }  
  }
  .sticky {
    position: -webkit-sticky;
    position: sticky;
    top: 0;
    z-index: 10;
  }
`;

const Background = styled.div`
  background: linear-gradient(90deg, rgb(210, 38, 48), rgb(234, 139, 0));
  @media print {
    background-image: none;
  }
`;

const getGlobalAlerts = ({ locale, data: { globalAlerts } = {} }) =>
  globalAlerts
    ? R.pipe(
        R.prop("edges"),
        R.map(R.prop("node")),
        R.filter(
          article =>
            locale === shortenLocaleName(article.node_locale) &&
            isActiveArticle(article) &&
            (R.isNil(article.alertLocations) ||
              R.isEmpty(article.alertLocations))
        )
      )(globalAlerts)
    : undefined;

const enhance = compose(
  withRouter,
  withProps(({ alerts, ...props }) => {
    const globalAlerts = getGlobalAlerts(props) || [];
    return {
      alerts: [...globalAlerts, ...(alerts || [])],
    };
  })
);

const Layout = enhance(
  ({
    breadcrumb,
    children,
    location,
    layout,
    locale,
    renderFooter = renderComponent(Footer)(),
    renderHeader = renderComponent(Header)(),
    renderAboveBreadcrumb,
    renderBottomContent,
    renderSidebar,
    i18n,
    t,
    alerts,
    data,
    title,
    pageTitle,
    breadcrumbTitle = pageTitle,
    description,
    headerImages,
    suppressNcuaLogo = false,
    suppressEhlLogo = false,
    isInvestments = false,
    Container = PageContainer,
    theme,
    ...props
  }) => {
    const hasHeaderImage = Boolean(headerImages && headerImages.length);
    const isFullPage = layout === "fullPage";

    const params = qs.parse(location.search.substr(1));

    const isContentOnly =
      layout === "contentOnly" || params["contentOnly"] === "true";
    const BackgroundContainer = isFullPage || isContentOnly ? Box : Background;

    const authentication = useContext(AuthenticationContext);

    const displayHeader = !isContentOnly && params["displayHeader"] !== "false";
    const displayFooter = !isContentOnly && params["displayFooter"] !== "false";

    return (
      <Fragment>
        <HelmetBlock title={title} description={description} key="helmet" />
        <BackgroundContainer>
          {authentication.currentError ? (
            <AuthenticationError
              error={authentication.currentError}
              onCancel={authentication.cancelAuth}
              key={authentication.currentError.id}
            />
          ) : null}
          <Box
            style={
              authentication.loginVisible
                ? {
                    backgroundColor: "#000000ee",
                  }
                : {}
            }
          >
            {renderHeader &&
              displayHeader &&
              renderHeader({
                data,
                t,
                locale,
                key: location.pathname,
                setLoginVisible: authentication.setLoginVisible,
                loginVisible: authentication.loginVisible,
                loggedIn: authentication.loggedIn,
                logOut: authentication.logOut,
                ...props,
              })}
            <Box
              pt={isContentOnly ? 0 : [2, 2, 3]}
              px={isContentOnly ? 0 : [0, 0, 3, 4, 6]}
              style={{
                opacity: authentication.loginVisible ? 0.2 : 1,
              }}
              printStyles={{ padding: 0 }}
            >
              <Alerts alerts={alerts} t={t} />
              {hasHeaderImage && (
                <HeaderSlider
                  key="header-slider"
                  images={headerImages}
                  parentTheme={theme}
                  {...PageContainer.roundedCorners}
                  imageProps={{
                    ...PageContainer.roundedCorners,
                    mx: [2, 0, 0],
                  }}
                />
              )}
              <Container
                roundTopCorners={!(isContentOnly || hasHeaderImage)}
                themeName={theme}
              >
                {renderAboveBreadcrumb && renderAboveBreadcrumb()}
                {breadcrumb && (
                  <Breadcrumb
                    breadcrumb={breadcrumb}
                    theme={theme}
                    pt={2}
                    px={[3, 3, 4]}
                    currentPageTitle={breadcrumbTitle}
                  />
                )}
                <Flex
                  flexDirection={["column", "column", "column", "row"]}
                  printStyles={{ display: "block" }}
                >
                  <Box flex={[undefined, undefined, undefined, 5]}>
                    {children}
                  </Box>
                  {renderSidebar && (
                    <Box
                      flex={[undefined, undefined, undefined, 2]}
                      pl={[3, 3, 4]}
                      pb={4}
                      pt={[0, 0, 56]}
                    >
                      <Box
                        borderColor="divider"
                        borderWidth={2}
                        borderTop={true}
                        flex={1}
                        pt={2}
                      />
                      <Box pr={[3, 3, 4]}>{renderSidebar()}</Box>
                    </Box>
                  )}
                </Flex>
                {renderBottomContent && (
                  <Box>
                    <Divider side="left" />
                    {renderBottomContent()}
                  </Box>
                )}
              </Container>
            </Box>
          </Box>
        </BackgroundContainer>
        {renderFooter &&
          displayFooter &&
          renderFooter({
            locale,
            location,
            data,
            t,
            isInvestments,
            includeNcuaLogo: !suppressNcuaLogo,
            includeEhlLogo: !suppressEhlLogo,
          })}
        <PixelTracker
          locationPathname={location && location.pathname}
          trackers={data && data.allContentfulTracker}
        />
      </Fragment>
    );
  }
);

export default translate("translations")(Layout);

export const query = graphql`
  fragment LayoutQuery on RootQueryType {
    ...MenuQuery
    ...FooterQuery
    site {
      siteMetadata {
        siteUrl
        siteTitle
      }
    }
    logo: imageSharp(id: { regex: "/fcfcu-logo.png/" }) {
      sizes(maxWidth: 272) {
        ...GatsbyImageSharpSizes_withWebp_noBase64
      }
    }
    whiteLogo: imageSharp(id: { regex: "/fcfcu-logo-white-stacked.png/" }) {
      resolutions(width: 150, height: 50) {
        ...GatsbyImageSharpResolutions_withWebp_noBase64
      }
    }
    whiteLogoBallState: imageSharp(
      id: { regex: "/ball-state-logo-white-stacked.png/" }
    ) {
      resolutions(width: 158, height: 50) {
        ...GatsbyImageSharpResolutions_withWebp_noBase64
      }
    }
    globalAlerts: allContentfulArticle(
      filter: { embedStyle: { eq: "Alert" } }
    ) {
      edges {
        node {
          ...ArticleEmbed
          publicationDate
          unpublishDate
          node_locale
          alertLocations {
            id
          }
        }
      }
    }
    ...TrackerQuery
  }
`;



// WEBPACK FOOTER //
// ./src/components/Layout/Layout.js